/**
 * Filter Rule Editor
 *
 * Edits a single filter rule (left comparator right)
 */

import React from 'react';
import { X } from 'lucide-react';
import type { FilterRule, Comparator } from '../../types/selectionV2.ts';

interface FilterRuleEditorProps {
  rule: FilterRule;
  onChange: (rule: FilterRule) => void;
  onRemove: () => void;
  formulas?: Array<{ id: string; name: string; formula: string }>;
}

const FilterRuleEditor: React.FC<FilterRuleEditorProps> = ({
  rule,
  onChange,
  onRemove,
  formulas = []
}) => {
  const comparators: { value: Comparator; label: string }[] = [
    { value: '>', label: '>' },
    { value: '>=', label: '>=' },
    { value: '<', label: '<' },
    { value: '<=', label: '<=' },
    { value: '==', label: '==' },
    { value: '!=', label: '!=' },
    { value: 'between', label: 'between' }
  ];

  return (
    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-md border border-gray-200">
      {/* Left side: formula selector */}
      <div className="flex-1">
        <select
          value={rule.left}
          onChange={(e) => onChange({ ...rule, left: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
        >
          <option value="">-- Select Formula --</option>
          {formulas.map(f => (
            <option key={f.id} value={`formula:${f.id}`}>
              {f.name}
            </option>
          ))}
        </select>
        {!rule.left && (
          <p className="text-xs text-gray-500 mt-1">Select a formula from Step 2 to filter on</p>
        )}
      </div>

      {/* Comparator */}
      <select
        value={rule.comparator}
        onChange={(e) => onChange({ ...rule, comparator: e.target.value as Comparator })}
        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {comparators.map(c => (
          <option key={c.value} value={c.value}>{c.label}</option>
        ))}
      </select>

      {/* Right value(s) */}
      {rule.comparator === 'between' ? (
        <>
          <input
            type="number"
            value={rule.right ?? 0}
            onChange={(e) => onChange({ ...rule, right: parseFloat(e.target.value) })}
            placeholder="min"
            className="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <span className="text-gray-500 text-sm">and</span>
          <input
            type="number"
            value={rule.right2 ?? 0}
            onChange={(e) => onChange({ ...rule, right2: parseFloat(e.target.value) })}
            placeholder="max"
            className="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </>
      ) : (
        <input
          type="number"
          value={rule.right ?? 0}
          onChange={(e) => onChange({ ...rule, right: parseFloat(e.target.value) })}
          placeholder="value"
          className="w-32 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      )}

      {/* NA Policy Toggle */}
      <label className="flex items-center gap-2 px-2 py-1 bg-white border border-gray-300 rounded-md text-sm whitespace-nowrap cursor-pointer hover:bg-gray-50">
        <input
          type="checkbox"
          checked={rule.naPolicy === 'drop'}
          onChange={(e) => onChange({ ...rule, naPolicy: e.target.checked ? 'drop' : 'worst' })}
          className="rounded"
        />
        <span className="text-gray-700">Exclude NAs</span>
      </label>

      {/* Remove button */}
      <button
        onClick={onRemove}
        className="p-2 text-red-500 hover:bg-red-50 rounded-md transition-colors flex-shrink-0"
        title="Remove rule"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default FilterRuleEditor;
